'use client';

import { styled } from 'styled-components';
import cs from 'classnames';
import TextLink from '@dvd/components/TextLink';
import type { ReactNodes, CtaProps } from '@types';

type MultiColumnProps = {
  /**
   * `MultiColumnChild` components to display in the grid layout.
   * - Reference the `MultiColumnChild` component to see the props used for each `MultiColumnChild` component.
   *
   * Rules:
   * - Each `MultiColumnChild` should either include (or omit) the headline and description.
   */
  children: ReactNodes<2> | ReactNodes<3> | ReactNodes<4>;
  /**
   * Optional description at top of the module.
   */
  description?: string;
  /**
   * Optional headline at top of the module.
   */
  headline?: string;
  /**
   * Optional CTA for the module
   */
  cta?: CtaProps;
  /**
   * Optional padding displayed at the bottom of module; enabled by default.
   */
  bottomPadding?: boolean;
  /**
   * Optional padding displayed at the top of module; enabled by default.
   */
  topPadding?: boolean;
};

const MultiColumnHeading = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  text-align: center;
`;

const MultiColumnContent = styled.div`
  display: grid;
  margin-block-start: ${({ theme }) => theme.spacing[3]};
  ${({ theme }) => theme.grid.columns.small}

  ${({ theme }) => theme.mq.minWidth.large} {
    ${({ theme }) => theme.grid.columns.large}
    row-gap: ${({ theme }) => theme.spacing[3]};
  }
`;

const MultiColumnTextContainer = styled.div`
  display: grid;
  ${({ theme }) => theme.grid.columns.small}

  ${({ theme }) => theme.mq.minWidth.large} {
    ${({ theme }) => theme.grid.columns.large}
  }
`;

const MultiColumnContainer = styled.div<{
  $topPadding: boolean;
  $bottomPadding: boolean;
}>`
  padding-block: ${({ theme }) => theme.spacing[3]};
  ${({ $bottomPadding }) => !$bottomPadding && `padding-block-end: 0;`}
  ${({ $topPadding }) => !$topPadding && `padding-block-start: 0;`}

  ${MultiColumnHeading} {
    grid-column: auto / span 2;
  }

  ${MultiColumnContent} > * {
    grid-column: auto / span 2;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding-block: ${({ theme }) => theme.spacing[4]};
    ${({ $bottomPadding }) => !$bottomPadding && `padding-block-end: 0;`}
    ${({ $topPadding }) => !$topPadding && `padding-block-start: 0;`}

    ${MultiColumnHeading} {
      grid-column: 3 / span 8;
    }

    &.twoUp ${MultiColumnContent} > * {
      grid-column: 2 / span 5;

      &:last-child {
        grid-column: 7 / span 5;
      }
    }

    &.threeUp ${MultiColumnContent} > * {
      grid-column: auto / span 4;
    }

    &.fourUp ${MultiColumnContent} > * {
      grid-column: auto / span 6;
    }
  }
`;

const getVariant = (childrenLength: 2 | 3 | 4) => {
  switch (childrenLength) {
    case 2:
      return 'twoUp';
    case 3:
      return 'threeUp';
    case 4:
    default:
      return 'fourUp';
  }
};

/**
 * A module that displays a `MultiColumnChild` in a multi-column grid layout.
 * - Reference the `MultiColumnChild` component to see the props used for each `MultiColumnChild` component.
 *
 * Grid layout behavior:
 * - 2 `MultiColumnChild`: Two-column layout (6 columns each on large screens)
 * - 3 `MultiColumnChild`: Three-column layout (4 columns each on large screens)
 * - 4 `MultiColumnChild`: Two x Two layout (6 columns each on large screens)
 *
 * Rules:
 * - Must contain exactly 2, 3, or 4 `MultiColumnChild` components.
 */
const MultiColumn = ({
  children,
  cta = null,
  description,
  headline,
  topPadding = true,
  bottomPadding = true,
}: MultiColumnProps) => {
  const variant = getVariant(children.length);

  return (
    <MultiColumnContainer
      $bottomPadding={bottomPadding}
      $topPadding={topPadding}
      className={cs(variant)}
    >
      <MultiColumnTextContainer>
        {headline || description || cta ? (
          <MultiColumnHeading>
            <h2>{headline}</h2>
            <p>{description}</p>
            {cta ? <TextLink cta={cta} /> : null}
          </MultiColumnHeading>
        ) : null}
      </MultiColumnTextContainer>
      <MultiColumnContent>{children}</MultiColumnContent>
    </MultiColumnContainer>
  );
};

export default MultiColumn;
